import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Package, Plus, Edit3, Trash2, Save, Check, Search,
  Image as ImageIcon, ExternalLink, Upload, ChevronDown, ChevronRight, Link2,
} from "lucide-react";
import { toast } from "sonner";
import InfoTooltip from "@/components/admin/InfoTooltip";
import AdminBrandsPage from "./AdminBrandsPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const titleCase = (s) =>
  (s || "")
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const FIELD_DEFS = [
  { key: "name", label: "Name" },
  { key: "category", label: "Category", type: "category-select" },
  { key: "brand_id", label: "Brand", type: "brand-select" },
  { key: "image_url", label: "Image", type: "image" },
  { key: "buy_link", label: "Buy link" },
  { key: "description", label: "Description" },
  { key: "price", label: "Price (USD)", type: "number" },
];

/**
 * AdminMasterProductsPage - Iter 9.31 rebuild
 *
 * Goals:
 *   - Group rows by category (collapsible)
 *   - Category field is a relational <Select> bound to admin/category-master
 *   - Brand field is "select-or-create" combobox bound to existing brands
 *   - Image is a Cloudinary upload, NOT a free URL field
 *   - "Used by" panel: shows looks/services that link to a product
 */
export default function AdminMasterProductsPage() {
  const [products, setProducts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [creatingCat, setCreatingCat] = useState(""); // category id we're creating in
  const [newDraft, setNewDraft] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [usageOpen, setUsageOpen] = useState(null); // product_id
  const [usage, setUsage] = useState(null);

  const auth = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }),
    []
  );

  const loadCategories = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/category-master`, { headers: auth() });
      if (!r.ok) return;
      const d = await r.json();
      setCategories(
        (d.masters || []).map((m) => ({
          category_id: m.category_id,
          label: titleCase(m.label || m.category_id),
        }))
      );
    } catch {
      // silent
    }
  }, [auth]);

  const loadBrands = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/brands`, { headers: auth() });
      if (!r.ok) return;
      const d = await r.json();
      setBrands(d.brands || []);
    } catch {
      // silent
    }
  }, [auth]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/master-products`, { headers: auth() });
      if (r.ok) {
        const d = await r.json();
        setProducts(d.products || []);
        setSuggestions(d.suggestions || []);
      }
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    loadCategories();
    loadBrands();
    fetchAll();
  }, [loadCategories, loadBrands, fetchAll]);

  const knownBrands = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.brand && set.add(p.brand));
    return Array.from(set).sort();
  }, [products]);

  // Iter 9.40 Slice C⁶ - nested grouping: Category → Brand → Products.
  // `grouped[cid].brands[brand]` returns the row list for that (category, brand)
  // pair; `grouped[cid].total` is the running count for the category badge.
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const out = {};
    products.forEach((p) => {
      if (
        term &&
        !(p.name || "").toLowerCase().includes(term) &&
        !(p.brand || "").toLowerCase().includes(term)
      ) {
        return;
      }
      const cid = p.category || "uncategorized";
      const brand = p.brand || "Unbranded";
      if (!out[cid]) out[cid] = { brands: {}, total: 0 };
      if (!out[cid].brands[brand]) out[cid].brands[brand] = [];
      out[cid].brands[brand].push(p);
      out[cid].total += 1;
    });
    return out;
  }, [products, search]);

  const allCategoryIds = useMemo(() => {
    const ids = new Set([
      ...categories.map((c) => c.category_id),
      ...Object.keys(grouped),
    ]);
    return Array.from(ids).sort();
  }, [categories, grouped]);

  const labelFor = (cid) => {
    const m = categories.find((c) => c.category_id === cid);
    return m?.label || titleCase(cid);
  };

  const startEdit = (p) => {
    setEditingId(p.product_id);
    setEditDraft({ ...p });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async () => {
    try {
      const body = { ...editDraft };
      if (body.price !== undefined && body.price !== "") body.price = Number(body.price);
      const r = await fetch(`${API}/admin/master-products/${editingId}`, {
        method: "PUT",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (r.ok) {
        toast.success("Saved");
        cancelEdit();
        fetchAll();
      } else {
        toast.error("Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    const r = await fetch(`${API}/admin/master-products/${id}`, {
      method: "DELETE",
      headers: auth(),
    });
    if (r.ok) {
      toast.success("Deleted");
      fetchAll();
    } else {
      toast.error("Delete failed");
    }
  };

  const startNew = (cid) => {
    setCreatingCat(cid);
    setNewDraft({ category: cid });
  };

  const createNew = async () => {
    if (!newDraft.name?.trim()) {
      toast.error("Name required");
      return;
    }
    const body = { ...newDraft };
    if (body.price !== undefined && body.price !== "") body.price = Number(body.price);
    const r = await fetch(`${API}/admin/master-products`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      toast.success("Created");
      setCreatingCat("");
      setNewDraft({});
      fetchAll();
    } else {
      toast.error("Create failed");
    }
  };

  const toggleGroup = (cid) =>
    setCollapsed((c) => ({ ...c, [cid]: !c[cid] }));

  const openUsage = async (product_id) => {
    if (usageOpen === product_id) {
      setUsageOpen(null);
      setUsage(null);
      return;
    }
    setUsageOpen(product_id);
    setUsage(null);
    try {
      const r = await fetch(`${API}/admin/master-products/usage/${product_id}`, {
        headers: auth(),
      });
      if (r.ok) setUsage(await r.json());
    } catch {
      // silent
    }
  };

  const promoteSuggestionToNewMaster = async (sub) => {
    const newProd = {
      name: sub.name,
      category: sub.category || null,
      image_url: sub.image_url || null,
      buy_link: sub.link || null,
      description: sub.description || null,
      brand: sub.brand || null,
      status: "active",
    };
    const r = await fetch(`${API}/admin/master-products`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(newProd),
    });
    if (r.ok) {
      const d = await r.json();
      const pid = d.product?.product_id;
      if (pid) {
        await fetch(
          `${API}/admin/master-products/${pid}/promote-suggestion/${sub.submission_id}`,
          { method: "POST", headers: auth() }
        );
      }
      toast.success("Linked to master");
      fetchAll();
    } else {
      toast.error("Promote failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-master-products-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Package className="w-5 h-5" /> Master Products
            <InfoTooltip
              label="Master Products"
              description="Canonical, admin-managed product catalog. Hosts pick from this when adding products to looks. Grouped by category. Click 'Used by' to see linked looks/services."
              source="db.master_products"
              where_synced={["Host Products tab", "Plan a Service product picker", "Preference card linked_products"]}
            />
          </h1>
          <p className="text-xs text-gray-500">
            Grouped by category. Category, brand, and image are relational fields.
          </p>
        </div>
      </div>

      <Tabs defaultValue="catalog">
        <TabsList>
          <TabsTrigger value="catalog" data-testid="mp-tab-catalog">
            Catalog ({products.length})
          </TabsTrigger>
          <TabsTrigger value="brands" data-testid="mp-tab-brands">
            Brands ({brands.length})
          </TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="mp-tab-suggestions">
            Suggestions ({suggestions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog">
          <div className="mb-3 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search master products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="mp-search"
            />
          </div>

          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3">
              {allCategoryIds.map((cid) => {
                const catGroup = grouped[cid] || { brands: {}, total: 0 };
                const brandKeys = Object.keys(catGroup.brands).sort((a, b) =>
                  a === "Unbranded" ? 1 : b === "Unbranded" ? -1 : a.localeCompare(b)
                );
                const totalRows = catGroup.total;
                const isCollapsed = !!collapsed[cid];
                return (
                  <Card key={cid} data-testid={`mp-group-${cid}`}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(cid)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        {isCollapsed ? (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-semibold text-sm">{labelFor(cid)}</span>
                        <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">
                          {totalRows}
                        </Badge>
                        <span className="text-[10px] text-gray-400 ml-1">
                          {brandKeys.length} brand{brandKeys.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          startNew(cid);
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1 cursor-pointer"
                        data-testid={`mp-add-${cid}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Add product
                      </span>
                    </button>

                    {!isCollapsed && (
                      <CardContent className="pt-0 space-y-2">
                        {creatingCat === cid && (
                          <div className="border border-amber-200 bg-amber-50 rounded p-3">
                            <p className="text-xs font-semibold mb-2">
                              New product in {labelFor(cid)}
                            </p>
                            <ProductFieldsForm
                              draft={newDraft}
                              setDraft={setNewDraft}
                              categories={categories}
                              knownBrands={knownBrands}
                              brands={brands}
                              testidPrefix="new-mp"
                            />
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" onClick={createNew} data-testid="new-mp-save">
                                <Save className="w-3.5 h-3.5 mr-1" /> Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setCreatingCat("");
                                  setNewDraft({});
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}

                        {totalRows === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-4">
                            No products in {labelFor(cid)} yet.
                          </p>
                        ) : (
                          brandKeys.map((bKey) => {
                            const bRows = catGroup.brands[bKey];
                            const brandKey = `${cid}::${bKey}`;
                            const brandCollapsed = !!collapsed[brandKey];
                            const brandLogo = brands.find((x) => x.name === bKey || x.brand_id === bKey)?.logo_url;
                            return (
                              <div key={bKey} className="border border-stone-100 rounded-md" data-testid={`mp-brand-group-${cid}-${bKey}`}>
                                <button
                                  type="button"
                                  onClick={() => toggleGroup(brandKey)}
                                  className="w-full px-3 py-2 flex items-center gap-2 hover:bg-stone-50 rounded-t-md"
                                >
                                  {brandCollapsed ? (
                                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                  {brandLogo && (
                                    <img src={brandLogo} alt={bKey} className="w-4 h-4 rounded object-contain border border-stone-100" />
                                  )}
                                  <span className="text-xs font-semibold text-stone-700">{bKey}</span>
                                  <Badge className="bg-stone-100 text-stone-600 border-0 text-[10px] ml-1">
                                    {bRows.length}
                                  </Badge>
                                </button>
                                {!brandCollapsed && (
                                  <div className="p-2 pt-0 space-y-1">
                                    {bRows.map((p) =>
                                      editingId === p.product_id ? (
                                        <div
                                          key={p.product_id}
                                          className="border rounded p-3 bg-gray-50"
                                        >
                                          <ProductFieldsForm
                                            draft={editDraft}
                                            setDraft={setEditDraft}
                                            categories={categories}
                                            knownBrands={knownBrands}
                                            brands={brands}
                                            testidPrefix={`edit-mp-${p.product_id}`}
                                          />
                                          <div className="flex gap-2 mt-2">
                                            <Button
                                              size="sm"
                                              onClick={saveEdit}
                                              data-testid={`save-mp-${p.product_id}`}
                                            >
                                              <Save className="w-3.5 h-3.5 mr-1" /> Save
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={cancelEdit}>
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div key={p.product_id}>
                                          <div
                                            className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded"
                                            data-testid={`mp-row-${p.product_id}`}
                                          >
                                            {p.image_url ? (
                                              <img
                                                src={p.image_url}
                                                className="w-10 h-10 rounded object-cover flex-shrink-0"
                                                alt=""
                                              />
                                            ) : (
                                              <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                <ImageIcon className="w-4 h-4 text-gray-400" />
                                              </div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium truncate flex items-center gap-2">
                                                {p.name}
                                                {(() => {
                                                  const b = brands.find((x) => x.brand_id === p.brand_id);
                                                  if (b?.logo_url) {
                                                    return (
                                                      <img
                                                        src={b.logo_url}
                                                        alt={b.name}
                                                        className="w-4 h-4 rounded object-contain border"
                                                        title={b.name}
                                                      />
                                                    );
                                                  }
                                                  return null;
                                                })()}
                                                {(p.brand_id || p.brand) && (
                                                  <span className="ml-1 text-xs text-gray-400">
                                                    {brands.find((x) => x.brand_id === p.brand_id)?.name || p.brand}
                                                  </span>
                                                )}
                                              </p>
                                              {p.buy_link && (
                                                <a
                                                  href={p.buy_link}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-[11px] text-blue-600 inline-flex items-center gap-0.5 truncate"
                                                >
                                                  <ExternalLink className="w-2.5 h-2.5" />{" "}
                                                  {p.buy_link.slice(0, 40)}
                                                </a>
                                              )}
                                            </div>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => openUsage(p.product_id)}
                                              data-testid={`usage-mp-${p.product_id}`}
                                            >
                                              <Link2 className="w-3.5 h-3.5 mr-1" /> Used by
                                            </Button>
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              onClick={() => startEdit(p)}
                                              data-testid={`edit-mp-btn-${p.product_id}`}
                                            >
                                              <Edit3 className="w-4 h-4" />
                                            </Button>
                                            <Button
                                              size="icon"
                                              variant="ghost"
                                              onClick={() => deleteRow(p.product_id)}
                                              data-testid={`delete-mp-btn-${p.product_id}`}
                                            >
                                              <Trash2 className="w-4 h-4 text-red-500" />
                                            </Button>
                                          </div>
                                          {usageOpen === p.product_id && (
                                            <div
                                              className="ml-12 mr-3 mb-2 border-l-2 border-indigo-200 pl-3 py-2 text-xs"
                                              data-testid={`usage-panel-${p.product_id}`}
                                            >
                                              {!usage ? (
                                                <p className="text-gray-400">Loading usage...</p>
                                              ) : usage.look_count === 0 && usage.service_count === 0 ? (
                                                <p className="text-gray-500">
                                                  Not linked to any look or service yet.
                                                </p>
                                              ) : (
                                                <div className="space-y-1">
                                                  <p className="text-gray-700">
                                                    Linked in <strong>{usage.look_count}</strong>{" "}
                                                    look(s),{" "}
                                                    <strong>{usage.service_count}</strong> service(s).
                                                  </p>
                                                  {usage.linked_looks?.slice(0, 5).map((l) => (
                                                    <p key={l.card_id} className="text-gray-500">
                                                      · Look: {l.title} ({l.category})
                                                    </p>
                                                  ))}
                                                  {usage.linked_services?.slice(0, 5).map((s) => (
                                                    <p key={s.service_id} className="text-gray-500">
                                                      · Service: {s.name} ({s.category})
                                                    </p>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="brands">
          <AdminBrandsPage />
        </TabsContent>

        <TabsContent value="suggestions">
          {loading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : suggestions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No pending suggestions.</p>
          ) : (
            <div className="space-y-2">
              {suggestions.map((s) => (
                <Card key={s.submission_id} className="hover:shadow-sm">
                  <CardContent className="p-3 flex items-center gap-3">
                    {s.image_url ? (
                      <img
                        src={s.image_url}
                        className="w-12 h-12 rounded object-cover flex-shrink-0"
                        alt=""
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <ImageIcon className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{s.name}</p>
                      <p className="text-xs text-gray-500 truncate">submitted by {s.user_id}</p>
                      {s.description && (
                        <p className="text-xs text-gray-500 truncate">{s.description}</p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={() => promoteSuggestionToNewMaster(s)}
                      data-testid={`promote-suggestion-${s.submission_id}`}
                    >
                      <Check className="w-3.5 h-3.5 mr-1" /> Promote
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProductFieldsForm({ draft, setDraft, categories, knownBrands, brands = [], testidPrefix }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/cloudinary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const url = d.url || d.secure_url;
        if (url) {
          setDraft({ ...draft, image_url: url });
          toast.success("Image uploaded");
        } else {
          toast.error("Upload failed");
        }
      } else {
        toast.error("Upload failed");
      }
    } catch {
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {FIELD_DEFS.map((f) => {
        if (f.type === "category-select") {
          return (
            <div key={f.key}>
              <label className="text-xs text-gray-500 block mb-0.5">{f.label}</label>
              <Select
                value={draft[f.key] || ""}
                onValueChange={(v) => setDraft({ ...draft, [f.key]: v })}
              >
                <SelectTrigger data-testid={`${testidPrefix}-${f.key}`}>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        if (f.type === "brand-select") {
          const selected = brands.find((b) => b.brand_id === draft[f.key]);
          return (
            <div key={f.key}>
              <label className="text-xs text-gray-500 block mb-0.5">
                {f.label}{" "}
                <span className="text-gray-400">(select from brand database)</span>
              </label>
              <div className="flex items-center gap-2">
                {selected?.logo_url && (
                  <img
                    src={selected.logo_url}
                    alt={selected.name}
                    className="w-6 h-6 rounded object-contain border"
                  />
                )}
                <Select
                  value={draft[f.key] || ""}
                  onValueChange={(v) => setDraft({ ...draft, [f.key]: v })}
                >
                  <SelectTrigger data-testid={`${testidPrefix}-${f.key}`}>
                    <SelectValue placeholder="Select brand" />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((b) => (
                      <SelectItem key={b.brand_id} value={b.brand_id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        }
        if (f.type === "brand-combobox") {
          return (
            <div key={f.key}>
              <label className="text-xs text-gray-500 block mb-0.5">
                {f.label}{" "}
                <span className="text-gray-400">(select or type new)</span>
              </label>
              <Input
                list={`${testidPrefix}-brand-list`}
                value={draft[f.key] || ""}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                placeholder="MAC, Mielle, etc."
                data-testid={`${testidPrefix}-${f.key}`}
              />
              <datalist id={`${testidPrefix}-brand-list`}>
                {knownBrands.map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>
          );
        }
        if (f.type === "image") {
          return (
            <div key={f.key} className="sm:col-span-2">
              <label className="text-xs text-gray-500 block mb-0.5">{f.label}</label>
              <div className="flex items-center gap-3">
                <div className="relative w-14 h-14 group">
                  {draft.image_url ? (
                    <>
                      <img
                        src={draft.image_url}
                        alt=""
                        className="w-14 h-14 rounded object-cover border"
                      />
                      <button
                        type="button"
                        onClick={() => setDraft({ ...draft, image_url: "" })}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 hover:bg-black flex items-center justify-center text-white transition-opacity opacity-0 group-hover:opacity-100"
                        aria-label="Remove image (revert to default)"
                        title="Remove image (revert to default)"
                        data-testid={`${testidPrefix}-image-remove`}
                      >
                        <span className="text-[10px] font-bold">×</span>
                      </button>
                    </>
                  ) : (
                    <div className="w-14 h-14 rounded bg-gray-100 border flex items-center justify-center">
                      <ImageIcon className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 flex flex-col gap-1">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                    data-testid={`${testidPrefix}-image-file`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    data-testid={`${testidPrefix}-image-upload-btn`}
                  >
                    {uploading ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5 mr-1" />
                    )}
                    {draft.image_url ? "Replace image" : "Upload image"}
                  </Button>
                  {!draft.image_url && (
                    <p className="text-[10px] text-gray-400">
                      Empty = uses brand logo or category icon as default.
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div key={f.key}>
            <label className="text-xs text-gray-500 block mb-0.5">{f.label}</label>
            <Input
              type={f.type === "number" ? "number" : "text"}
              value={draft[f.key] ?? ""}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              data-testid={`${testidPrefix}-${f.key}`}
            />
          </div>
        );
      })}

      {/* Slice E.4 - multi-shade editor */}
      <div className="sm:col-span-2 mt-1 border-t border-stone-100 pt-3">
        <ShadesEditor
          shades={draft.shades || []}
          onChange={(next) => setDraft({ ...draft, shades: next })}
          testidPrefix={testidPrefix}
        />
      </div>
    </div>
  );
}

/**
 * ShadesEditor - Slice E.4
 *
 * One product can hold N shades (e.g. MAC Studio Fix Fluid → NC15, NC20, NC25, NC40, …).
 * Each row carries {name, hex, image_url}. The hex is what bubbles up to the /me preference grid.
 */
function ShadesEditor({ shades, onChange, testidPrefix }) {
  const fileRefs = useRef({});
  const [uploadingIdx, setUploadingIdx] = useState(-1);

  const update = (i, patch) => {
    const next = shades.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(shades.filter((_, idx) => idx !== i));
  const add = () => onChange([...shades, { name: "", hex: "", image_url: "" }]);
  const uploadShade = async (i, file) => {
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/cloudinary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        update(i, { image_url: d.url || d.secure_url });
        toast.success("Shade image uploaded");
      } else toast.error("Upload failed");
    } catch {
      toast.error("Upload error");
    } finally {
      setUploadingIdx(-1);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-shades`}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs text-gray-500 font-medium">Shades ({shades.length})</label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="h-7 text-[11px]" data-testid={`${testidPrefix}-shades-add`}>
          <Plus className="w-3 h-3 mr-1" />Add shade
        </Button>
      </div>
      {shades.length === 0 ? (
        <p className="text-[11px] text-stone-400">Optional - add one row per shade name (e.g. NC15, NC20, NC25). Each row's hex is the canonical color shown on /me.</p>
      ) : (
        <div className="space-y-1.5">
          {shades.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5 p-1.5 rounded border border-stone-100 bg-white" data-testid={`${testidPrefix}-shade-row-${i}`}>
              <input
                type="color"
                value={s.hex || "#cc8e62"}
                onChange={(e) => update(i, { hex: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer border border-stone-200 shrink-0"
                title="Pick swatch hex"
                data-testid={`${testidPrefix}-shade-${i}-hex`}
              />
              <Input
                value={s.name || ""}
                onChange={(e) => update(i, { name: e.target.value })}
                placeholder="Shade name (NC15, 240, …)"
                className="h-7 text-xs flex-1"
                data-testid={`${testidPrefix}-shade-${i}-name`}
              />
              <Input
                value={s.hex || ""}
                onChange={(e) => update(i, { hex: e.target.value })}
                placeholder="#cc8e62"
                className="h-7 text-xs font-mono w-24"
                data-testid={`${testidPrefix}-shade-${i}-hex-text`}
              />
              {s.image_url ? (
                <img src={s.image_url} alt="" className="w-7 h-7 rounded object-cover border" />
              ) : null}
              <input
                ref={(el) => { fileRefs.current[i] = el; }}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadShade(i, e.target.files[0])}
              />
              <Button type="button" size="sm" variant="ghost" className="h-7 px-1.5" disabled={uploadingIdx === i} onClick={() => fileRefs.current[i]?.click()} title="Upload shade swatch image" data-testid={`${testidPrefix}-shade-${i}-image-btn`}>
                {uploadingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 px-1.5 text-stone-400 hover:text-red-500" onClick={() => remove(i)} title="Remove shade" data-testid={`${testidPrefix}-shade-${i}-del`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
